import { useEffect } from 'react'
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import ShelvingConfigurator from '@/components/shelving-configurator'
import LoginPage from '@/pages/login'
import SetupPage from '@/pages/setup'
import AdminPanel from '@/pages/admin'
import ResellerPanel from '@/pages/reseller'
import { useLanguageStore, Language } from '@/lib/language-store'
import { useAuthStore } from '@/lib/auth-store'
import { useDomainStore } from '@/lib/domain-store'

const VALID_LANGS = ['en', 'nl', 'de'] as const

function ConfiguratorRoute() {
  const [searchParams] = useSearchParams()
  const { setLanguage } = useLanguageStore()
  const { reseller } = useAuthStore()
  const { domainReseller } = useDomainStore()

  useEffect(() => {
    const langParam = searchParams.get('lang')
    if (langParam && VALID_LANGS.includes(langParam as Language)) {
      setLanguage(langParam as Language)
    } else if (reseller?.default_language && VALID_LANGS.includes(reseller.default_language as Language)) {
      setLanguage(reseller.default_language as Language)
    } else if (domainReseller?.default_language && VALID_LANGS.includes(domainReseller.default_language as Language)) {
      setLanguage(domainReseller.default_language as Language)
    }
  }, [searchParams, setLanguage, reseller, domainReseller])

  return (
    <main className="flex min-h-screen flex-col">
      <div className="flex-1 w-full p-0">
        <ShelvingConfigurator />
      </div>
    </main>
  )
}

function AdminRoute() {
  const { profile, loading } = useAuthStore()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-[#212b4f]" />
    </div>
  )
  if (!profile) return <Navigate to="/login" replace />
  if (profile.role !== 'admin') return <Navigate to="/" replace />
  return <AdminPanel />
}

function ResellerRoute() {
  const { profile, loading } = useAuthStore()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-[#212b4f]" />
    </div>
  )
  if (!profile) return <Navigate to="/login" replace />
  if (profile.role !== 'reseller') return <Navigate to="/" replace />
  return <ResellerPanel />
}

function App() {
  const { initialize: initAuth } = useAuthStore()
  const { initialize: initDomain } = useDomainStore()

  useEffect(() => {
    initAuth()
    initDomain()
  }, [initAuth, initDomain])

  return (
    <Routes>
      <Route path="/" element={<ConfiguratorRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/admin" element={<AdminRoute />} />
      <Route path="/reseller" element={<ResellerRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
